import { useState, useEffect } from "react";
import {
  Card, Table, Stack, TableRow, TableBody, TableCell, Container, Typography,
  TableContainer, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, InputAdornment, Chip,
} from "@mui/material";
import { Edit as EditIcon } from "@mui/icons-material";
import Page from "src/components/Page";
import Scrollbar from "src/components/Scrollbar";
import SearchNotFound from "src/components/SearchNotFound";
import ListHead from "src/components/common/ListHead";
import MyPagination from "src/components/common/MyPagination";
import { asyncRequest } from "src/services/network";
import { alert } from "src/services/alert";
import Confirm from "src/components/common/ConfirmDialog";
import dayjs from "dayjs";

// 等级背景色配置（游戏段位风格）
const GRADE_COLORS = {
  1: "#FFF3E0", // 热血青铜 - 浅橙/铜色
  2: "#E8EAF6", // 不屈白银 - 浅银/蓝灰色
  3: "#FFF8E1", // 英勇黄金 - 浅金/黄色
  4: "#E1F5FE", // 无畏铂金 - 浅蓝/铂金色
  5: "#F3E5F5", // 荣耀钻石 - 浅紫/钻石色
};

// 等级名称映射
const GRADE_NAMES = {
  1: "热血青铜",
  2: "不屈白银",
  3: "英勇黄金",
  4: "无畏铂金",
  5: "荣耀钻石",
};

// 开发/测试环境检测：本地调试或测试环境跳过所有时间限制
const isDevOrTest = () => {
  if (process.env.NODE_ENV === 'development') return true;
  const host = window.location.hostname;
  return host === 'localhost' || host === '127.0.0.1' || host.includes('.test.') || host.includes('dev');
};

const STATUS_MAP = {
  0: { label: "预约中", color: "warning" },
  1: { label: "抢购中", color: "success" },
  2: { label: "已结束", color: "default" },
};

const TABLE_HEAD = [
  { id: "id", label: "ID", alignRight: false },
  { id: "grade_name", label: "场次名称", alignRight: false },
  { id: "time_range", label: "时间范围", alignRight: false },
  { id: "coin_range", label: "金豆范围", alignRight: false },
  { id: "order_num", label: "订单数量", alignRight: false },
  { id: "reserve_detail", label: "预约详情", alignRight: false },
  { id: "status", label: "状态", alignRight: false },
  { id: "action", label: "操作", alignRight: false },
];

export default function SessionList() {
  const [filter, setFilter] = useState({
    date: dayjs().format("YYYY-MM-DD"), // 默认当天
    pageNum: 1,
    pageSize: 20,
  });
  const [list, setList] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState(null);
  const [editType, setEditType] = useState(""); // 'order' 或 'reserve'

  // 加载等级列表
  const [grades, setGrades] = useState([]);
  useEffect(() => {
    asyncRequest({ path: "/jingxi/grade/all", data: {} }).then(({ data }) => {
      setGrades(data || []);
    }).catch(() => {});
  }, []);

  const getGradeName = (gradeId) => {
    // 优先使用配置中的等级名称
    const grade = grades.find((g) => g.id === gradeId);
    if (grade) return grade.grade_name;
    
    // 如果配置中没有，使用游戏段位名称
    return GRADE_NAMES[gradeId] || `等级${gradeId}`;
  };

  const getGradeColor = (gradeId) => {
    return GRADE_COLORS[gradeId] || "#FAFAFA";
  };

  const fetchList = async () => {
    setLoading(true);
    try {
      const data = {
        date: filter.date,
        pageNum: filter.pageNum,
        pageSize: filter.pageSize,
      };
      const res = await asyncRequest({ path: "/jingxi/game/sessions/list", data });
      if (res.code === 1) {
        setList(res.data || []);
        setCount(res.count || 0);
      } else {
        alert(res.msg || "获取列表失败", "error");
      }
    } catch (err) {
      console.error(err);
      alert("获取列表失败", "error");
    } finally {
      setLoading(false);
    }
  };

  // 日期变化时自动刷新
  useEffect(() => {
    fetchList();
  }, [filter.date, filter.pageNum, filter.pageSize]);

  const handleToggleStatus = async (id, currentStatus, startTime) => {
    // 开发/测试环境跳过时间限制
    if (!isDevOrTest()) {
      const start = new Date(startTime);
      const now = new Date();
      const diffMinutes = (start - now) / 1000 / 60;
      if (diffMinutes < 30) {
        return alert("场次开场前30分钟停止修改状态", "warning");
      }
    }
    
    // 校验状态
    if (currentStatus === 2) {
      return alert("已结束的场次无法修改状态", "warning");
    }
    
    const newStatus = currentStatus === 1 ? 0 : 1;
    const actionText = newStatus === 1 ? "启用" : "停用";
    
    Confirm({
      message: `确认${actionText}该场次？`,
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/sessions/update-status",
            data: { id, status: newStatus },
          });
          if (res.code === 1) {
            alert(`${actionText}成功`, "success");
            fetchList();
          } else {
            alert(res.msg || `${actionText}失败`, "error");
          }
        } catch (err) {
          alert(`${actionText}失败`, "error");
        }
      },
    });
  };

  const handleGenerate = async () => {
    Confirm({
      message: "确认生成今日场次？预约数据基于日期+等级，重新生成不会影响用户预约。",
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/sessions/generate",
            data: {},
          });
          if (res.code === 1) {
            alert("生成成功", "success");
            fetchList();
          } else {
            // 显示后端返回的具体错误信息（如时间不足30分钟）
            alert(res.msg || "生成失败", "error");
          }
        } catch (err) {
          alert("生成失败", "error");
        }
      },
    });
  };

  const handleRobotReserve = async () => {
    Confirm({
      message: "确认执行机器人重新预约？将为所有活跃机器人预约今日未预约的场次。",
      async onConfirm() {
        try {
          const res = await asyncRequest({
            path: "/jingxi/game/sessions/robot-reserve",
            data: {},
          });
          if (res.code === 1) {
            const d = res.data || {};
            alert(
              `机器人预约完成：成功${d.success || 0}人，新增预约${d.reservations || 0}条`,
              "success"
            );
            fetchList();
          } else {
            alert(res.msg || "操作失败", "error");
          }
        } catch (err) {
          alert("操作失败", "error");
        }
      },
    });
  };

  const handleViewDetail = async (id) => {
    try {
      const res = await asyncRequest({
        path: "/jingxi/game/sessions/detail",
        data: { id },
      });
      if (res.code === 1) {
        setDetailData(res.data);
        setDetailDialog(true);
      } else {
        alert(res.msg || "获取详情失败", "error");
      }
    } catch (err) {
      alert("获取详情失败", "error");
    }
  };

  const handleEditOrder = (row) => {
    // 开发/测试环境跳过时间限制
    if (!isDevOrTest()) {
      const startTime = new Date(row.start_time);
      const now = new Date();
      const diffMinutes = (startTime - now) / 1000 / 60;
      if (diffMinutes < 10) {
        alert("场次开场前10分钟停止修改，无法修改订单数量", "warning");
        return;
      }
    }
    
    setEditType("order");
    setEditData({
      id: row.id,
      order_num: row.order_num || 0,
      system_order_num: row.system_order_num || 0,
    });
    setEditDialog(true);
  };

  const handleEditReserve = (row) => {
    // 开发/测试环境跳过时间限制
    if (!isDevOrTest()) {
      const startTime = new Date(row.start_time);
      const now = new Date();
      const diffMinutes = (startTime - now) / 1000 / 60;
      if (diffMinutes < 10) {
        alert("场次开场前10分钟停止修改，无法修改预约人数", "warning");
        return;
      }
    }
    
    setEditType("reserve");
    setEditData({
      id: row.id,
      reserve_num: row.reserve_num || 0,
      actual_reserve_num: row.actual_reserve_num || 0,
      display_reserve_num: row.display_reserve_num || 0,
    });
    setEditDialog(true);
  };

  const handleSaveEdit = async () => {
    try {
      const path = editType === "order"
        ? "/jingxi/game/sessions/update-order-num"
        : "/jingxi/game/sessions/update-reserve-num";
      const res = await asyncRequest({ path, data: editData });
      if (res.code === 1) {
        alert("修改成功", "success");
        setEditDialog(false);
        fetchList();
      } else {
        alert(res.msg || "修改失败", "error");
      }
    } catch (err) {
      alert("修改失败", "error");
    }
  };

  const renderStatus = (status) => {
    const config = STATUS_MAP[status];
    if (!config) return "-";
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant={status === 1 ? "filled" : "outlined"}
      />
    );
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return "-";
    const d = new Date(timeStr);
    return `${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
  };

  const canEditSession = (startTime) => {
    // 开发/测试环境跳过时间限制
    if (isDevOrTest()) return true;
    // 校验时间：必须在开场前10分钟之前
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = (start - now) / 1000 / 60;
    return diffMinutes >= 10;
  };

  const canToggleStatus = (startTime) => {
    // 开发/测试环境跳过时间限制
    if (isDevOrTest()) return true;
    // 校验时间：必须在开场前30分钟之前
    const start = new Date(startTime);
    const now = new Date();
    const diffMinutes = (start - now) / 1000 / 60;
    return diffMinutes >= 30;
  };

  return (
    <Page title="场次列表">
      <Container maxWidth="2000px">
        <Card sx={{ p: 3 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
            <Typography variant="h4">场次列表</Typography>
            <Stack direction="row" spacing={2} alignItems="center">
              <TextField
                size="small"
                type="date"
                label="选择日期"
                InputLabelProps={{ shrink: true }}
                value={filter.date}
                onChange={(e) => setFilter({ ...filter, date: e.target.value, pageNum: 1 })}
              />
              <Button variant="contained" onClick={handleGenerate} color="primary">
                生成今日场次
              </Button>
              <Button variant="outlined" onClick={handleRobotReserve} color="secondary">
                机器人重新预约
              </Button>
            </Stack>
          </Stack>

          {loading ? (
            <Stack alignItems="center" sx={{ py: 5 }}>加载中...</Stack>
          ) : list.length === 0 ? (
            <SearchNotFound searchQuery={filter.date} />
          ) : (
            <>
              <Scrollbar>
                <TableContainer>
                  <Table>
                    <ListHead headLabel={TABLE_HEAD} />
                    <TableBody>
                      {list.map((row) => (
                        <TableRow
                          key={row.id}
                          hover
                          sx={{ backgroundColor: getGradeColor(row.grade_id) }}
                        >
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ cursor: "pointer", color: "primary.main", textDecoration: "underline" }}
                              onClick={() => handleViewDetail(row.id)}
                            >
                              {row.id}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack
                              sx={{ cursor: "pointer" }}
                              onClick={() => handleViewDetail(row.id)}
                            >
                              <Typography variant="body2" fontWeight="bold" color="primary.main">
                                {getGradeName(row.grade_id)}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {row.name || "-"}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            {formatTime(row.start_time)} ~ {formatTime(row.end_time)}
                          </TableCell>
                          <TableCell>
                            {row.num_floor} ~ {row.num_max}
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.3}>
                              <Typography variant="body2">挂单数量：{row.user_order_num || 0}</Typography>
                              <Typography variant="body2">系统放单：{row.system_order_num || 0}</Typography>
                              <Typography variant="body2" fontWeight="bold">订单总数：{row.order_num || 0}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>
                            <Stack spacing={0.3}>
                              <Typography variant="body2" fontWeight="bold">实际有效预约：{row.actual_reserve_num || 0}</Typography>
                              <Typography variant="body2" color="primary">展示预约：{row.display_reserve_num || 0}</Typography>
                              <Typography variant="body2">人工预约（总数 | 有效数）：{row.user_reserve_total || 0} | {row.user_reserve_valid || 0}</Typography>
                              <Typography variant="body2">机器人预约（总数 | 有效数）：{row.robot_reserve_total || 0} | {row.robot_reserve_valid || 0}</Typography>
                              <Typography variant="body2" color="text.secondary">机器人派单数：{row.robot_dispatch_num || 0}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell>{renderStatus(row.status)}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              {canEditSession(row.start_time) ? (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEditOrder(row)}
                                  >
                                    改订单
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleEditReserve(row)}
                                  >
                                    改预约
                                  </Button>
                                </>
                              ) : (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                  title="开场前10分钟停止修改"
                                >
                                  已锁定
                                </Button>
                              )}
                              {row.status !== 2 && canToggleStatus(row.start_time) ? (
                                <Button
                                  size="small"
                                  variant={row.status === 1 ? "outlined" : "contained"}
                                  color={row.status === 1 ? "warning" : "success"}
                                  onClick={() => handleToggleStatus(row.id, row.status, row.start_time)}
                                >
                                  {row.status === 1 ? "停用" : "启用"}
                                </Button>
                              ) : row.status !== 2 ? (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled
                                  title="开场前30分钟停止修改状态"
                                >
                                  已锁定
                                </Button>
                              ) : null}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <MyPagination
                count={count}
                rowsPerPage={filter.pageSize}
                page={filter.pageNum}
                onPageChange={(val) => setFilter({ ...filter, pageNum: val })}
                onRowsPerPageChange={(val) => setFilter({ ...filter, pageSize: val, pageNum: 1 })}
              />
            </>
          )}
        </Card>

        <Dialog open={detailDialog} onClose={() => setDetailDialog(false)} maxWidth="md" fullWidth>
          <DialogTitle>场次详情</DialogTitle>
          <DialogContent>
            {detailData && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                <Typography variant="h6" color="primary">
                  {getGradeName(detailData.grade_id)} - {detailData.name}
                </Typography>
                <Stack direction="row" spacing={4}>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">基本信息</Typography>
                    <Typography>场次ID：{detailData.id}</Typography>
                    <Typography>等级：{getGradeName(detailData.grade_id)}</Typography>
                    <Typography>类型：{detailData.type === 1 ? "金豆" : "银豆"}</Typography>
                    <Typography>状态：{renderStatus(detailData.status)}</Typography>
                  </Stack>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">时间信息</Typography>
                    <Typography>开始时间：{detailData.start_time}</Typography>
                    <Typography>结束时间：{detailData.end_time}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={4}>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">订单信息</Typography>
                    <Typography>总订单数：{detailData.order_num || 0}</Typography>
                    <Typography>用户挂单：{detailData.user_order_num || 0}</Typography>
                    <Typography>系统放单：{detailData.system_order_num || 0}</Typography>
                  </Stack>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">预约详情</Typography>
                    <Typography>实际有效预约：{detailData.actual_reserve_num || 0}</Typography>
                    <Typography>展示预约：{detailData.display_reserve_num || 0}</Typography>
                    <Typography>人工预约（总数 | 有效数）：{detailData.user_reserve_total || 0} | {detailData.user_reserve_valid || 0}</Typography>
                    <Typography>机器人预约（总数 | 有效数）：{detailData.robot_reserve_total || 0} | {detailData.robot_reserve_valid || 0}</Typography>
                    <Typography>不达标(余额不足)：{detailData.invalid_reserve_num || 0}</Typography>
                  </Stack>
                </Stack>
                <Stack direction="row" spacing={4}>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">金豆范围</Typography>
                    <Typography>下限：{detailData.num_floor}</Typography>
                    <Typography>上限：{detailData.num_max}</Typography>
                  </Stack>
                  <Stack spacing={1} flex={1}>
                    <Typography variant="subtitle2" color="text.secondary">机器人派单</Typography>
                    <Typography>派单数量：{detailData.robot_dispatch_num || 0}</Typography>
                  </Stack>
                </Stack>
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailDialog(false)}>关闭</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editType === "order" ? "修改系统放单数量" : "修改预约美化基数"}
          </DialogTitle>
          <DialogContent>
            {editData && (
              <Stack spacing={2} sx={{ mt: 1 }}>
                {editType === "order" ? (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      当前总订单数：{editData.order_num}
                    </Typography>
                    <TextField
                      fullWidth
                      label="系统放单数量"
                      type="number"
                      value={editData.system_order_num}
                      onChange={(e) =>
                        setEditData({ ...editData, system_order_num: parseInt(e.target.value) || 0 })
                      }
                    />
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      实际有效预约：{editData.actual_reserve_num}，展示预约 = 有效预约 + 美化基数
                    </Typography>
                    <TextField
                      fullWidth
                      label="预约美化基数"
                      type="number"
                      value={editData.display_reserve_num}
                      onChange={(e) =>
                        setEditData({ ...editData, display_reserve_num: parseInt(e.target.value) || 0 })
                      }
                    />
                  </>
                )}
              </Stack>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialog(false)}>取消</Button>
            <Button onClick={handleSaveEdit} variant="contained">保存</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
